import { useEffect, useState, useCallback, useRef } from 'react'

interface VersionInfo {
  version: string
  buildTime: string
}

interface UseVersionCheckResult {
  hasNewVersion: boolean
  refresh: () => void
}

const POLL_INTERVAL = 120000 // 2 minutes in milliseconds

/**
 * Hook to check if a new version is deployed
 * @param initialVersion - The version from SSR when the page was rendered
 */
export function useVersionCheck(initialVersion: string): UseVersionCheckResult {
  const [hasNewVersion, setHasNewVersion] = useState(false)
  const pollingRef = useRef<NodeJS.Timeout | null>(null)

  const checkVersion = useCallback(async () => {
    try {
      const response = await fetch('/api/version', {
        cache: 'no-store',
      })

      if (!response.ok) {
        return
      }

      const data: VersionInfo = await response.json()
      const currentVersion = data.version

      // Compare server version with the version we loaded with
      if (initialVersion !== currentVersion) {
        setHasNewVersion(true)
      }
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.debug('Version check failed:', error)
    }
  }, [initialVersion])

  const refresh = useCallback(async () => {
    // If a new service worker is waiting, activate it before reloading
    // so the reload picks up the new SW (important in standalone PWA mode)
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration()
        if (registration?.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' })
          // controllerchange listener in PWARegister will trigger the reload
          return
        }
      } catch {
        // Fall through to manual reload
      }
    }
    window.location.reload()
  }, [])

  useEffect(() => {
    // Initial check
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch sets state in callback, not synchronously in effect body
    checkVersion()

    // Set up polling
    pollingRef.current = setInterval(checkVersion, POLL_INTERVAL)

    // Cleanup on unmount
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
    }
  }, [checkVersion])

  return { hasNewVersion, refresh }
}

